import {DataGranularity, HistoryMetric} from '../../api/types';
import * as Dates from '../Dates';
import {DateTimeConfig} from '../Dates';
import * as Time from '../Time';
import {isRainMetric} from './RainMetricUtils';
import {CalendarAggregation, HistoryBaseMetric} from './types';

/**
 * Returns the minimum allowed granularity for rain and lightning metrics.
 * Non-rain/non-lightning metrics return 'max' (finest resolution).
 */
const getMinGranularity = (metric: HistoryBaseMetric): DataGranularity => {
  switch (metric) {
    case 'RR_1H':
    case 'LIGHTNING_STRIKE_COUNT':
      return 'H';
    case 'RR_3H':
      return '3H';
    case 'RR_6H':
      return '6H';
    case 'RR_12H':
    case 'RR_24H':
      return 'D';
    default:
      return 'max';
  }
};

export const getDefaultGranularity = (
  scale: Time.Scale,
  metric: HistoryBaseMetric,
) => {
  const minGran = getMinGranularity(metric);
  const isCumulative = isRainMetric(metric) || metric === 'LIGHTNING_STRIKE_COUNT';

  let granularity = 'H' as DataGranularity;
  if ([Dates.DAY, Dates.LAST_24_HOURS].includes(scale)) {
    if (!isCumulative) {
      granularity = 'max';
    } else {
      granularity = minGran;
    }
  } else if ([Dates.WEEK, Dates.LAST_7_DAYS].includes(scale)) {
    if (!isCumulative) {
      granularity = '3H';
    } else {
      // Use '6H' unless the metric's minimum granularity is already 'D'
      granularity = ['H', '3H', '6H'].includes(minGran) ? '6H' : 'D';
    }
  } else if ([Dates.MONTH, Dates.LAST_30_DAYS].includes(scale)) {
    granularity = 'D';
  } else if (scale === Dates.YEAR) {
    granularity = isCumulative ? 'W' : 'M';
  } else {
    granularity = 'H';
  }
  return granularity;
};

export const getGranularityChoicesFromScale = (
  scale: Time.Scale,
  metric: HistoryBaseMetric,
) => {
  const isCumulative = isRainMetric(metric) || metric === 'LIGHTNING_STRIKE_COUNT';
  const minGran = getMinGranularity(metric);

  // Ordered granularities for cumulative metrics (finest → coarsest)
  const GRAN_ORDER: DataGranularity[] = ['H', '3H', '6H', 'D', 'W'];
  const filterFromMin = (choices: DataGranularity[]): DataGranularity[] => {
    const minIdx = GRAN_ORDER.indexOf(minGran);
    return choices.filter(c => GRAN_ORDER.indexOf(c) >= minIdx);
  };

  let granularityChoices = ['H'] as DataGranularity[];
  if (scale === Dates.DAY) {
    if (!isCumulative) {
      granularityChoices = ['max', 'H', '3H'];
    } else {
      granularityChoices = filterFromMin(['H', '3H', '6H', 'D']);
    }
  } else if (scale === Dates.LAST_24_HOURS) {
    if (!isCumulative) {
      granularityChoices = ['max', 'H'];
    } else {
      granularityChoices = filterFromMin(['H', '3H', '6H', 'D']);
    }
  } else if ([Dates.WEEK, Dates.LAST_7_DAYS].includes(scale)) {
    if (!isCumulative) {
      granularityChoices = ['H', '3H', '6H', 'D'];
    } else {
      granularityChoices = filterFromMin(['H', '3H', '6H', 'D']);
    }
  } else if ([Dates.MONTH, Dates.LAST_30_DAYS].includes(scale)) {
    granularityChoices = ['D', 'W'];
  } else if (scale === Dates.YEAR) {
    granularityChoices = ['W', 'M'];
  } else {
    granularityChoices = ['H'];
  }
  return granularityChoices;
};

export const getCalendarGranularityFromScale = (
  scale: Time.Scale,
  metric?: string,
) => {
  const minGran =
    metric && (isRainMetric(metric) || metric === 'LIGHTNING_STRIKE_COUNT')
      ? getMinGranularity(metric as HistoryBaseMetric)
      : 'H';

  let granularity = 'H' as DataGranularity;
  switch (scale) {
    case Dates.DAY:
      granularity = minGran === 'max' ? 'H' : minGran;
      break;
    case Dates.WEEK:
      granularity = minGran === 'max' ? 'H' : minGran;
      break;
    case Dates.MONTH:
      granularity = 'D';
      break;
    case 'year':
      granularity = 'D';
      break;
    default:
      granularity = minGran === 'max' ? 'H' : minGran;
  }
  return granularity;
};

export const getDateFormatFromGranularity = (
  granularity: DataGranularity,
  dateTimeConfig: DateTimeConfig,
) => {
  if (['H', 'max'].includes(granularity)) {
    if (dateTimeConfig.is_24_hour) {
      return '<b>%H:%M</b> (%A %e %B %Y)';
    } else {
      return '<b>%l:%M %P</b> (%A %e %B %Y)';
    }
  }
  if (granularity === 'D') {
    return '%A %e %B %Y';
  }
  if (granularity === 'W') {
    return '%e %B %Y';
  }
  if (granularity === 'M') {
    return '%B %Y';
  }
};

export const getTypeListFromDataTypeAndGranularity = (
  metric: HistoryBaseMetric,
  granularity: DataGranularity,
  // Sub-hourly-sampling stations (all non-SYNOP sources) get true per-hour
  // min/max from the backend aggregation matrix — see
  // Station.hasAggregatedHourlyHistory. SYNOP hourly rows are point
  // observations, so min/max at H would be meaningless there.
  hourlyAggregations = false,
) => {
  const withMinMax = (granularities: DataGranularity[]) =>
    granularities.includes(granularity) ||
    (hourlyAggregations && granularity === 'H');

  const typeList = [] as HistoryMetric[];
  if (metric === 'U') {
    typeList.push('U');
    if (withMinMax(['D', 'W', 'M'])) {
      typeList.push('U_MIN');
      typeList.push('U_MAX');
    }
  } else if (metric === 'T') {
    typeList.push('T');
    if (withMinMax(['D', 'W', 'M'])) {
      typeList.push('T_MIN');
      typeList.push('T_MAX');
    }
  } else if (metric === 'FF') {
    typeList.push('FF');
    typeList.push('DD');
    typeList.push('FXY');
    typeList.push('DXY');
  } else if (metric === 'SLP') {
    typeList.push('SLP');
    if (withMinMax(['D', 'W', 'M'])) {
      typeList.push('SLP_MIN');
      typeList.push('SLP_MAX');
    }
  } else if (metric === 'SOLAR_RADIATION') {
    typeList.push('SOLAR_RADIATION');
    if (withMinMax(['3H', '6H', 'D', 'W', 'M'])) {
      typeList.push('SOLAR_RADIATION_MIN');
      typeList.push('SOLAR_RADIATION_MAX');
    }
  } else if (metric === 'VIS') {
    typeList.push('VIS');
    if (withMinMax(['3H', '6H', 'D', 'W', 'M'])) {
      typeList.push('VIS_MIN');
      typeList.push('VIS_MAX');
    }
  } else if (metric === 'UV') {
    typeList.push('UV');
    if (['H', '3H', '6H', 'D', 'W', 'M'].includes(granularity)) {
      typeList.push('UV_MIN');
      typeList.push('UV_MAX');
    }
  } else {
    typeList.push(metric);
  }
  return typeList;
};

const CALENDAR_AGGREGATION_INDEX: Record<CalendarAggregation, number> = {
  avg: 0,
  min: 1,
  max: 2,
};

/**
 * Whether the calendar heatmap can offer an avg/min/max switch for this
 * metric at this granularity, i.e. whether the positional array carries
 * <metric>_MIN / <metric>_MAX series. Scoped to D/W/M granularity, plus H
 * when the station's hourly buckets are true aggregations of sub-hourly
 * samples (`hourlyAggregations`, see Station.hasAggregatedHourlyHistory).
 */
export const hasCalendarAggregations = (
  metric: HistoryBaseMetric | undefined,
  granularity: DataGranularity,
  hourlyAggregations = false,
) => {
  const allowedGranularity =
    ['D', 'W', 'M'].includes(granularity) ||
    (hourlyAggregations && granularity === 'H');
  if (!metric || !allowedGranularity) {
    return false;
  }
  const typeList = getTypeListFromDataTypeAndGranularity(
    metric,
    granularity,
    hourlyAggregations,
  );
  return (
    typeList.includes(`${metric}_MIN` as HistoryMetric) &&
    typeList.includes(`${metric}_MAX` as HistoryMetric)
  );
};

/**
 * Position of the displayed value in a calendar day-cell's positional
 * array. Keeps the legacy Netatmo 'Wind' special case at index 1.
 */
export const getCalendarListPos = (
  type: string,
  aggregation?: CalendarAggregation,
) => {
  if (type === 'Wind') {
    return 1;
  }
  return CALENDAR_AGGREGATION_INDEX[aggregation ?? 'avg'];
};

export const getMainTypeFromDataType = (metric: HistoryBaseMetric) => {
  const typeList = [] as string[];
  if (isRainMetric(metric)) {
    typeList.push(metric);
  } else if (metric === 'FF') {
    typeList.push('FF');
  } else {
    typeList.push(metric);
  }
  return typeList;
};

export const getPointFormat = (metric: HistoryBaseMetric) => {
  if (metric === 'FF') {
    return '<span style="color:{series.color}">\u25CF</span> {series.name}: <span class="tooltip-value"> <b>{point.y}</b> </span> ({point.z})<br>';
  }
  return '<span style="color:{series.color}">\u25CF</span> {series.name}: <span class="tooltip-value"> <b>{point.y}</b> </span><br>';
};

export const formatMetricLabel = (metric: HistoryBaseMetric) => {
  return metric.toLowerCase();
};

export const getDataTypeLabel = (dataType: string) => {
  return dataType;
};

export const getDataTypePrecision = (metric: HistoryBaseMetric) => {
  let precision = 0;
  switch (metric) {
    case 'T':
      precision = 1;
      break;
    case 'U':
      precision = 0;
      break;
    case 'SLP':
      precision = 0;
      break;
    case 'RR_1H':
    case 'RR_3H':
    case 'RR_6H':
    case 'RR_12H':
    case 'RR_24H':
      precision = 1;
      break;
    case 'SOLAR_RADIATION':
      precision = 0;
      break;
    case 'UV':
      precision = 1;
      break;
    case 'VIS':
      precision = 0;
      break;
    default:
      precision = 0;
  }
  return precision;
};

export const getChartTypeFromDataType = (
  dataType: HistoryBaseMetric,
  comparison: boolean,
) => {
  let chartType = 'spline';
  switch (dataType) {
    case 'T':
      chartType = 'spline';
      break;
    case 'U':
      chartType = 'spline';
      break;
    case 'SLP':
      chartType = 'spline';
      break;
    case 'RR_1H':
    case 'RR_3H':
    case 'RR_6H':
    case 'RR_12H':
    case 'RR_24H':
      chartType = 'column';
      break;
    case 'FF':
      chartType = 'spline';
      break;
    case 'SOLAR_RADIATION':
      chartType = comparison ? 'spline' : 'areaspline';
      break;
    case 'UV':
      chartType = 'spline';
      break;
    case 'VIS':
      chartType = 'spline';
      break;
    case 'AQI':
      chartType = 'spline';
      break;
    case 'LIGHTNING_STRIKE_COUNT':
      chartType = 'column';
      break;
    default:
      chartType = 'spline';
  }
  return chartType;
};

export const getChartComparisonChoiceList = (
  scale: Time.Scale,
  timestamp: number,
  timezone: string,
) => {
  if (scale === Dates.DAY) {
    return {
      [Dates.PREVIOUS_DAY]: {
        name: 'previousDay',
        format: Dates.formatTimestampFromScale(
          Dates.getPrevious(timestamp, timezone, Dates.DAY),
          timezone,
          scale,
        ),
      },
      [Dates.SAME_DAY_LAST_YEAR]: {
        name: 'sameDayLastYear',
        format: Dates.formatTimestampFromScale(
          Dates.getPrevious(timestamp, timezone, Dates.YEAR),
          timezone,
          scale,
        ),
      },
    };
  }
  if (scale === Dates.LAST_24_HOURS) {
    return {
      [Dates.PREVIOUS_24_HOURS]: {
        name: 'previous24Hours',
        format: `${Dates.formatDate(
          getComparisonChartStart(
            timezone,
            Dates.LAST_24_HOURS,
            timestamp,
            Dates.PREVIOUS_24_HOURS,
          ),
          timezone,
          'lll',
        )} - ${Dates.formatDate(
          getComparisonChartEnd(
            timezone,
            Dates.LAST_24_HOURS,
            timestamp,
            Dates.PREVIOUS_24_HOURS,
          ),
          timezone,
          'lll',
        )}`,
      },
    };
  }
  if (scale === Dates.WEEK) {
    return {
      [Dates.PREVIOUS_WEEK]: {
        name: 'previousWeek',
        format: Dates.formatTimestampFromScale(
          Dates.getPrevious(timestamp, timezone, Dates.WEEK),
          timezone,
          scale,
        ),
      },
    };
  }
  if (scale === Dates.LAST_7_DAYS) {
    return {
      [Dates.PREVIOUS_7_DAYS]: {
        name: 'previous7Days',
        format: `${Dates.formatDate(
          getComparisonChartStart(
            timezone,
            Dates.LAST_7_DAYS,
            timestamp,
            Dates.PREVIOUS_7_DAYS,
          ),
          timezone,
          'll',
        )} - ${Dates.formatDate(
          getComparisonChartEnd(
            timezone,
            Dates.LAST_7_DAYS,
            timestamp,
            Dates.PREVIOUS_7_DAYS,
          ),
          timezone,
          'll',
        )}`,
      },
    };
  }
  if (scale === Dates.MONTH) {
    return {
      [Dates.PREVIOUS_MONTH]: {
        name: 'previousMonth',
        format: Dates.formatTimestampFromScale(
          Dates.getPrevious(timestamp, timezone, Dates.MONTH),
          timezone,
          scale,
        ),
      },
      [Dates.SAME_MONTH_LAST_YEAR]: {
        name: 'sameMonthLastYear',
        format: Dates.formatTimestampFromScale(
          Dates.getPrevious(timestamp, timezone, Dates.YEAR),
          timezone,
          scale,
        ),
      },
    };
  }
  if (scale === Dates.LAST_30_DAYS) {
    return {
      [Dates.PREVIOUS_30_DAYS]: {
        name: 'previous30Days',
        format: `${Dates.formatDate(
          getComparisonChartStart(
            timezone,
            Dates.LAST_30_DAYS,
            timestamp,
            Dates.PREVIOUS_30_DAYS,
          ),
          timezone,
          'll',
        )} - ${Dates.formatDate(
          getComparisonChartEnd(
            timezone,
            Dates.LAST_30_DAYS,
            timestamp,
            Dates.PREVIOUS_30_DAYS,
          ),
          timezone,
          'll',
        )}`,
      },
    };
  }
  if (scale === Dates.YEAR) {
    return {
      [Dates.PREVIOUS_YEAR]: {
        name: 'previousYear',
        format: Dates.formatTimestampFromScale(
          Dates.getPrevious(timestamp, timezone, Dates.YEAR),
          timezone,
          scale,
        ),
      },
    };
  }
  return {};
};

export const getChartComparisonDefaultType = (scale: Time.Scale) => {
  if (scale === Dates.DAY) {
    return Dates.PREVIOUS_DAY;
  }
  if (scale === Dates.LAST_24_HOURS) {
    return Dates.PREVIOUS_24_HOURS;
  }
  if (scale === Dates.WEEK) {
    return Dates.PREVIOUS_WEEK;
  }
  if (scale === Dates.LAST_7_DAYS) {
    return Dates.PREVIOUS_7_DAYS;
  }
  if (scale === Dates.MONTH) {
    return Dates.PREVIOUS_MONTH;
  }
  if (scale === Dates.LAST_30_DAYS) {
    return Dates.PREVIOUS_30_DAYS;
  }
  if (scale === Dates.YEAR) {
    return Dates.PREVIOUS_YEAR;
  }
};

export const getComparisonChartStart = (
  timezone: string,
  scale: Time.Scale,
  timestamp: number,
  comparisonType: string,
) => {
  // scale 'day'
  if (comparisonType === Dates.PREVIOUS_DAY) {
    return Dates.getScaleStart(
      Dates.getPrevious(timestamp, timezone, Dates.DAY),
      timezone,
      scale,
    );
  }
  if (comparisonType === Dates.SAME_DAY_LAST_YEAR) {
    return Dates.getScaleStart(
      Dates.getPrevious(timestamp, timezone, Dates.YEAR),
      timezone,
      scale,
    );
  }
  // scale 'last24Hours'
  if (comparisonType === Dates.PREVIOUS_24_HOURS) {
    return Dates.shift(
      Dates.getScaleStart(timestamp, timezone, Dates.LAST_24_HOURS),
      timezone,
      -24,
      'hours',
    );
  }
  // scale 'week'
  if (comparisonType === Dates.PREVIOUS_WEEK) {
    return Dates.getScaleStart(
      Dates.getPrevious(timestamp, timezone, Dates.WEEK),
      timezone,
      scale,
    );
  }
  // scale 'last7Days'
  if (comparisonType === Dates.PREVIOUS_7_DAYS) {
    return Dates.shift(
      Dates.getScaleStart(timestamp, timezone, Dates.LAST_7_DAYS),
      timezone,
      -7,
      'days',
    );
  }
  // scale 'month'
  if (comparisonType === Dates.PREVIOUS_MONTH) {
    return Dates.getScaleStart(
      Dates.getPrevious(timestamp, timezone, Dates.MONTH),
      timezone,
      scale,
    );
  }
  if (comparisonType === Dates.SAME_MONTH_LAST_YEAR) {
    return Dates.getScaleStart(
      Dates.getPrevious(timestamp, timezone, Dates.YEAR),
      timezone,
      scale,
    );
  }
  // scale 'last30Days'
  if (comparisonType === Dates.PREVIOUS_30_DAYS) {
    return Dates.shift(
      Dates.getScaleStart(timestamp, timezone, Dates.LAST_30_DAYS),
      timezone,
      -30,
      'days',
    );
  }
  // scale 'year'
  if (comparisonType === Dates.PREVIOUS_YEAR) {
    return Dates.getScaleStart(
      Dates.getPrevious(timestamp, timezone, Dates.YEAR),
      timezone,
      scale,
    );
  }
};

export const getComparisonChartEnd = (
  timezone: string,
  scale: Time.Scale,
  timestamp: number,
  comparisonType: string,
) => {
  // scale 'day'
  if (comparisonType === Dates.PREVIOUS_DAY) {
    return Dates.getScaleEnd(
      Dates.getPrevious(timestamp, timezone, Dates.DAY),
      timezone,
      scale,
    );
  }
  if (comparisonType === Dates.SAME_DAY_LAST_YEAR) {
    return Dates.getScaleEnd(
      Dates.getPrevious(timestamp, timezone, Dates.YEAR),
      timezone,
      scale,
    );
  }
  // scale 'last24Hours'
  if (comparisonType === Dates.PREVIOUS_24_HOURS) {
    return Dates.getScaleStart(timestamp, timezone, Dates.LAST_24_HOURS) - 1;
  }
  // scale 'week'
  if (comparisonType === Dates.PREVIOUS_WEEK) {
    return Dates.getScaleEnd(
      Dates.getPrevious(timestamp, timezone, Dates.WEEK),
      timezone,
      scale,
    );
  }
  // scale 'last7Days'
  if (comparisonType === Dates.PREVIOUS_7_DAYS) {
    return Dates.getScaleStart(timestamp, timezone, Dates.LAST_7_DAYS) - 1;
  }
  // scale 'month'
  if (comparisonType === Dates.PREVIOUS_MONTH) {
    return Dates.getScaleEnd(
      Dates.getPrevious(timestamp, timezone, Dates.MONTH),
      timezone,
      scale,
    );
  }
  if (comparisonType === Dates.SAME_MONTH_LAST_YEAR) {
    return Dates.getScaleEnd(
      Dates.getPrevious(timestamp, timezone, Dates.YEAR),
      timezone,
      scale,
    );
  }
  // scale 'last30Days'
  if (comparisonType === Dates.PREVIOUS_30_DAYS) {
    return Dates.getScaleStart(timestamp, timezone, Dates.LAST_30_DAYS) - 1;
  }
  // scale 'year'
  if (comparisonType === Dates.PREVIOUS_YEAR) {
    return Dates.getScaleEnd(
      Dates.getPrevious(timestamp, timezone, Dates.YEAR),
      timezone,
      scale,
    );
  }
};

export const getComparisonOffset = (
  timezone: string,
  timestamp: number,
  comparisonType: string,
) => {
  const date = Dates.timezoneDateFromTimestamp(timestamp, timezone);

  if ([Dates.PREVIOUS_DAY, Dates.PREVIOUS_24_HOURS].includes(comparisonType)) {
    return timestamp - date.subtract(1, 'day').unix(); // 24 * 3600
  }
  if (comparisonType === Dates.SAME_DAY_LAST_YEAR) {
    return timestamp - date.subtract(1, 'year').unix(); // 24 * 3600
  }
  if ([Dates.PREVIOUS_WEEK, Dates.PREVIOUS_7_DAYS].includes(comparisonType)) {
    return timestamp - date.subtract(1, 'week').unix();
  }
  if (comparisonType === Dates.PREVIOUS_MONTH) {
    return timestamp - date.subtract(1, 'month').unix();
  }
  if (comparisonType === Dates.SAME_MONTH_LAST_YEAR) {
    return timestamp - date.subtract(1, 'year').unix();
  }
  if (comparisonType === Dates.PREVIOUS_30_DAYS) {
    return timestamp - date.subtract(30, 'days').unix();
  }
  if (comparisonType === Dates.PREVIOUS_YEAR) {
    return timestamp - date.subtract(12, 'months').unix();
  }
  return 0;
};
